/**
 * Der Bereich „Dateien" — `07` §7.7, dritter Hauptbereich.
 *
 * Das Dokument nennt vier Dinge: eine **auftragsbezogene Sicht statt
 * technischer Ordnerstruktur**, Nextcloud-Dateien, Paperless-Dokumente,
 * relevante E-Mail-Anhänge — und die Offlinekennzeichnung.
 *
 * Die auftragsbezogene Gliederung ist deshalb keine Sortieroption, sondern die
 * Struktur der Seite: Es gibt keine flache Dateiliste, weil ein Monteur nie
 * eine Datei sucht, sondern immer die Datei **zu etwas**. Wonach man sucht,
 * ist der Kunde oder der Auftrag; was man dann findet, sind zwei bis fünf
 * Sachen, und die passen in eine Gruppe.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router";

import { dienstadresse, quelladresse, systemname, findeAblage, findeAuftrag, findeKunde, nachbarn } from "@hwc/demo-data";
import type { Ablageobjekt } from "@hwc/demo-data";

import { useMonteur } from "../../src/Sitzung.js";
import { Leer, groesse } from "../../src/bausteine.js";

const OFFLINETEXT: Record<NonNullable<Ablageobjekt["offline"]>, string> = {
  vorgeladen: "offline verfügbar",
  "auf-wunsch": "nur mit Netz laden",
  "nur-online": "nur online",
};

const OFFLINETON: Record<NonNullable<Ablageobjekt["offline"]>, string> = {
  vorgeladen: "erfolg",
  "auf-wunsch": "neutral",
  "nur-online": "warnung",
};

export function Dateien() {
  const { bestand, monteur, online } = useMonteur();
  const [frage, setzeFrage] = useState("");
  const [nurOffline, setzeNurOffline] = useState(false);

  /**
   * Die Aufträge, an denen dieser Monteur Einsätze hat — und nur die.
   *
   * `07` §7.10 hält für E-Mail fest: „Monteure sehen nur bewusst mit dem
   * Auftrag verknüpfte Nachrichten." Dasselbe gilt sinngemäß für Dateien: Ein
   * Monteur bekommt die Ablage der Aufträge, auf denen er steht, und nicht den
   * ganzen Betrieb. Das ist hier eine Anzeigeentscheidung; die verbindliche
   * Prüfung macht nach `01` §1.11 das Quellsystem beim Öffnen.
   */
  const gruppen = useMemo(() => {
    const meine = bestand.einsaetze.filter(
      (e) => e.monteur?.opaqueId === monteur.ref.opaqueId,
    );
    const auftragsRefs = new Map<string, ReturnType<typeof findeAuftrag>>();
    for (const e of meine) {
      const a = findeAuftrag(bestand, e.auftrag);
      if (a) auftragsRefs.set(a.ref.opaqueId, a);
    }

    const q = frage.trim().toLowerCase();

    return [...auftragsRefs.values()]
      .map((auftrag) => {
        const kunde = findeKunde(bestand, auftrag!.kunde);
        const objekte = nachbarn(bestand, auftrag!.ref)
          .map((r) => findeAblage(bestand, r))
          .filter((o): o is Ablageobjekt => o !== undefined)
          .filter((o) => !nurOffline || o.offline === "vorgeladen");
        return { auftrag: auftrag!, kunde, objekte };
      })
      .filter((g) => g.objekte.length > 0)
      .filter((g) => {
        if (!q) return true;
        return (
          g.auftrag.titel.toLowerCase().includes(q)
          || g.auftrag.nummer.includes(q)
          || (g.kunde?.name.toLowerCase().includes(q) ?? false)
          || g.objekte.some((o) => o.name.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => b.auftrag.nummer.localeCompare(a.auftrag.nummer));
  }, [bestand, monteur, frage, nurOffline]);

  return (
    <>
      <h1>Dateien</h1>
      <p className="leise">
        Nach Auftrag, nicht nach Ordner. Was Sie hier sehen, hängt an den Aufträgen, auf
        denen Sie eingesetzt sind.
      </p>

      <input
        className="suchfeld"
        type="search"
        placeholder="Auftrag, Kunde oder Dateiname"
        aria-label="Dateien durchsuchen"
        value={frage}
        onChange={(e) => setzeFrage(e.target.value)}
      />

      <label className="schalter">
        <input
          type="checkbox"
          checked={nurOffline}
          onChange={(e) => setzeNurOffline(e.target.checked)}
        />
        Nur was offline verfügbar ist
      </label>

      {!online && !nurOffline && (
        <p className="marke marke--warten">
          Sie sind offline. Was nicht vorgeladen wurde, lässt sich jetzt nicht öffnen —
          es steht trotzdem in der Liste, damit Sie wissen, dass es existiert.
        </p>
      )}

      {gruppen.length === 0 ? (
        <Leer
          text={
            frage
              ? `Nichts gefunden zu „${frage}".`
              : nurOffline
                ? "Für Ihre Aufträge ist nichts vorgeladen."
                : "An Ihren Aufträgen hängen keine Dateien."
          }
        />
      ) : (
        gruppen.map((g) => (
          <section key={g.auftrag.ref.opaqueId} className="dateigruppe">
            <h2>
              {g.auftrag.nummer} — {g.auftrag.titel}
            </h2>
            <p className="leise">{g.kunde?.name}</p>
            <ul className="postenliste">
              {g.objekte.map((o) => {
                const erreichbar = online || o.offline === "vorgeladen";
                // Wie in der Einsatzansicht: nur mit Netz und nur mit
                // bekannter Adresse. Die Begründung steht dort ausgeschrieben.
                const ziel = erreichbar && dienstadresse(o.ref)
                  ? quelladresse(o.ref, {
                      name: o.name,
                      ...(o.pfad ? { pfad: o.pfad } : {}),
                    })
                  : undefined;
                return (
                  <li
                    key={o.ref.opaqueId}
                    className={erreichbar ? "" : "postenliste__fern"}
                  >
                    <span>
                      {ziel ? (
                        <a href={ziel} target="_blank" rel="noopener noreferrer">
                          {o.name}
                        </a>
                      ) : (
                        o.name
                      )}
                      <span
                        className={`abzeichen abzeichen--${OFFLINETON[o.offline ?? "nur-online"]}`}
                      >
                        {OFFLINETEXT[o.offline ?? "nur-online"]}
                      </span>
                      {/* Quellhinweis aus `PWA-005`, sekundär nach `07` §7.12. */}
                      <span className="leise"> · {systemname(o.ref)}</span>
                    </span>
                    <span>{groesse(o.groesseBytes)}</span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}

      <p className="leise fussnote">
        {/*
          `07` §7.12 trennt fachlich, nicht technisch. Der Systemname steht
          deshalb nicht an jeder Zeile, sondern einmal hier.
        */}
        Arbeitsdateien liegen in Nextcloud, archivierte Dokumente in Paperless. Beim
        Öffnen prüft das jeweilige System die Berechtigung erneut — ein Verweis allein
        gibt keinen Zugriff (<code>04</code> §4.2).{" "}
        <Link to="/einsaetze">Zu den Einsätzen</Link>
      </p>
    </>
  );
}
